import axios from 'axios';
import {
  CentricAccountTypeName,
  InternalChromeConfig,
} from '@amzn/aac-chrome-internal-api';
import { ThemeType } from '@amzn/storm-ui/dist/types/theme/ThemeProvider';
import { DEVICE_TYPE, PAGE_LAYOUT_TYPE, VIEW_TYPE } from 'src/app/slice';
import { logger } from './logger';
import {
  ChromeConfigurationPublisher,
  CurrencySwitcherPublisher,
} from './metrics';
import { ATC_EXTERNAL_TOGGLE } from '../constants';

/* eslint-disable-next-line */
declare var System: any;

export const initChromeData = (): Record<string, any> => {
  const script = document.getElementById('chrome-config');
  const metricName = 'InitialLoadSuccess';
  if (script == null) {
    logger.error('Cannot find chrome-config', { path: window.location.href });
    ChromeConfigurationPublisher.publishCounterMonitor(metricName, 0);
    return {};
  }
  try {
    const data = JSON.parse(script.innerHTML);
    ChromeConfigurationPublisher.publishCounterMonitor(metricName, 1);
    return data;
  } catch (error) {
    logger.error(`Fail to parse ${script.innerHTML}`);
    ChromeConfigurationPublisher.publishCounterMonitor(metricName, 0);
    return {};
  }
};

export const createAction = (type: string) => {
  return (payload: unknown): Record<string, unknown> => ({
    type,
    payload,
  });
};

export const scrollToActiveAccountLink = (
  parent?: Element | null,
  child?: Element | null,
): void => {
  if (!parent || !child) {
    return;
  }

  const parentRect = parent.getBoundingClientRect();
  const parentViewableArea = {
    height: parent.clientHeight,
    width: parent.clientWidth,
  };
  const childRect = child.getBoundingClientRect();
  const isViewable =
    childRect.top >= parentRect.top &&
    childRect.bottom <= parentRect.top + parentViewableArea.height;

  if (!isViewable) {
    const scrollTop = childRect.top - parentRect.top;
    const scrollBot = childRect.bottom - parentRect.bottom;
    if (Math.abs(scrollTop) < Math.abs(scrollBot)) {
      // closer to the top, so scroll to the element into the top of the view
      parent.scrollTop += scrollTop;
    } else {
      // closer to the bottom, so scroll to the element into the bottom of the view
      parent.scrollTop += scrollBot;
    }
  }
};

export const overrideSecondaryViewZIndex = (theme: ThemeType): ThemeType => ({
  ...theme,
  modal: {
    ...theme.modal,
    zIndex: 1002,
  },
});

export const overrideModalZIndex =
  (zIndex: number) =>
  (theme: ThemeType): ThemeType => ({
    ...theme,
    modal: {
      ...theme.modal,
      zIndex,
    },
  });

export const overrideDropdownMaxHeight =
  (maxHeight: string) =>
  (theme: ThemeType): ThemeType => ({
    ...theme,
    dropdown: {
      ...theme.dropdown,
      maxHeight,
    },
  });

export const isStandardPageLayoutType = (
  pageLayoutType: PAGE_LAYOUT_TYPE | string | undefined,
): boolean => {
  return (
    !!pageLayoutType &&
    (pageLayoutType === PAGE_LAYOUT_TYPE.STANDARD ||
      pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD)
  );
};

export const shouldRenderMenu = (
  viewType: VIEW_TYPE,
  pageLayoutType: PAGE_LAYOUT_TYPE | string | undefined,
): boolean => {
  if (viewType === VIEW_TYPE.DESKTOP) {
    return (
      pageLayoutType !== PAGE_LAYOUT_TYPE.LIGHT &&
      pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS
    );
  } else {
    return pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS;
  }
};

export const currencyofViewSwitch = async (
  currentCurrency: string,
  newCurrency: string,
): Promise<void> => {
  const startTime = performance.now();
  const refTag = `ref=cov_${currentCurrency}_${newCurrency}`;
  const postURL = `/billing/currencyOfView/change?${refTag}`;

  const postData = {
    newCurrencyOfView: newCurrency,
    oldCurrencyOfView: currentCurrency,
  };

  try {
    const currencyOfViewChange = await axios(postURL, {
      method: 'POST',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
      data: postData,
    });

    if (currencyOfViewChange) {
      CurrencySwitcherPublisher.publishCounterMonitor(
        'currecyOfViewSwitchSuccessCount',
        1,
      );

      window.location.reload();
    }
  } catch (error: any) {
    logger.error(
      'Error occurred while switching currency of view in AACChrome',
      error,
    );
    CurrencySwitcherPublisher.publishCounterMonitor(
      'currecyOfViewSwitchFailureCount',
      1,
    );
  } finally {
    CurrencySwitcherPublisher.publishTimerMonitor(
      'time',
      performance.now() - startTime,
    );
  }
};

export const showInternalView = (
  pageLayoutType: PAGE_LAYOUT_TYPE | string | undefined,
  isInternalView = false,
): boolean => {
  const isExternalToggleOn =
    window.sessionStorage.getItem(ATC_EXTERNAL_TOGGLE) === 'true';
  return (
    (pageLayoutType === PAGE_LAYOUT_TYPE.INTERNAL_STANDARD || isInternalView) &&
    !isExternalToggleOn
  );
};

export const getMetaTagContent = (metaTagName: string): string | null => {
  try {
    return JSON.parse(
      document
        .querySelector(`meta[name="${metaTagName}"]`)
        ?.getAttribute('content') ?? 'null',
    );
  } catch (e: any) {
    logger.error('fail to parse json for meta tag' + metaTagName, e);
    return null;
  }
};

export const getTrimInternalChromeConfig = ():
  | InternalChromeConfig
  | undefined => {
  try {
    return window.AACChrome.InternalAPI.InternalChromeConfigHandler.getInstance()
      .internalChromeConfig;
  } catch (e) {
    return {} as InternalChromeConfig;
  }
};

export const isMobileForTrim = (): boolean => {
  const internalChromeConfig = getTrimInternalChromeConfig();
  return (
    internalChromeConfig?.deviceType === DEVICE_TYPE.MOBILE &&
    internalChromeConfig?.mobileWebSupport === 'ENABLED' &&
    !internalChromeConfig?.forceDesktopSite
  );
};

export const centricAccountTypesWithOrder = [
  CentricAccountTypeName.GLOBAL,
  CentricAccountTypeName.MANAGER,
  CentricAccountTypeName.INDIVIDUAL,
];
